import React, { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faPause, faVolumeHigh, faSliders, faCog, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { getApiUrl } from '../../config/api';
import { AudioTrack } from './types';
import { downloadAudio } from '../../services/autoDownloadService';
import styles from './AudioModal.module.css';

interface AudioMixerProps {
  tracks: AudioTrack[];
  totalDuration: number;
  masterVolume: number;
  isMasterMuted: boolean;
  isVisible: boolean;
  onClose: () => void;
  projectId?: string;
  finalSoundAudio?: string; // Add finalSoundAudio prop
  isAudioOnlyWorkflow?: boolean; // Add flag to distinguish audio-only vs video workflows
}

export const AudioMixer: React.FC<AudioMixerProps> = ({
  tracks,
  totalDuration,
  masterVolume,
  isMasterMuted,
  isVisible,
  onClose,
  projectId,
  finalSoundAudio,
  isAudioOnlyWorkflow = false
}) => {
  const { t } = useTranslation('soundEditor');
  
  console.log('🎛️ AudioMixer rendered:', { 
    tracks: tracks.length, 
    isVisible, 
    totalDuration,
    finalSoundAudio: finalSoundAudio ? 'HAS DATA' : 'NO DATA'
  });

  const { showNotification } = useNotification();
  const { token } = useAuth();
  const [isMixing, setIsMixing] = useState(false);

  // Log all track details when modal loads
  React.useEffect(() => {
    if (isVisible && tracks.length > 0) {
      console.log('🎵 MODAL LOADED - Current Track States:');
      tracks.forEach((track, index) => {
        console.log(`Track ${index + 1}:`, {
          id: track.id,
          name: track.name,
          volume: track.volume,
          muted: track.muted || track.isMuted, // Handle both property names
          isMuted: track.isMuted,
          type: track.type,
          duration: track.duration,
          startTime: track.startTime
        });
      });
      console.log('🎛️ Master Settings:', {
        masterVolume,
        isMasterMuted
      });
    }
  }, [isVisible, tracks, masterVolume, isMasterMuted]);

  const handleMix = async () => {
    console.log('🚀 Merging audio tracks in browser...');
    setIsMixing(true);
    
    try {
      if (tracks.length === 0) {
        throw new Error('No tracks to merge');
      }

      // Create audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const sampleRate = audioContext.sampleRate;
      
      // Calculate the actual duration needed based ONLY on trimmed track end points
      // (ignore totalDuration as it may still reflect original untrimmed duration)
      const trackEndPoints = tracks.map(track => {
        const trimmedDuration = (track.trimEnd || track.duration || 0) - (track.trimStart || 0);
        return (track.startTime || 0) + trimmedDuration;
      });
      
      const maxDuration = trackEndPoints.length > 0 ? Math.max(...trackEndPoints) : totalDuration;
      
      const totalSamples = Math.ceil(maxDuration * sampleRate);
      const numberOfChannels = 2; // Stereo
      
      // Create output buffer
      const mergedBuffer = audioContext.createBuffer(numberOfChannels, totalSamples, sampleRate);
      const leftChannel = mergedBuffer.getChannelData(0);
      const rightChannel = mergedBuffer.getChannelData(1);
      
      console.log(`🎵 Buffer Duration Calculation:`, {
        originalTotalDuration: totalDuration.toFixed(2) + 's',
        calculatedFromTracks: maxDuration.toFixed(2) + 's',
        usingDuration: maxDuration.toFixed(2) + 's'
      });
      console.log(`🎵 Merging ${tracks.length} tracks into ${maxDuration.toFixed(2)}s buffer`);
      
      // Log track end points for debugging
      console.log('📊 Track End Points:', tracks.map(track => {
        const trimmedDuration = (track.trimEnd || track.duration || 0) - (track.trimStart || 0);
        const endPoint = (track.startTime || 0) + trimmedDuration;
        return {
          name: track.name || track.id,
          startTime: track.startTime || 0,
          trimStart: track.trimStart || 0,
          trimEnd: track.trimEnd || track.duration || 0,
          originalDuration: track.duration || 0,
          trimmedDuration: trimmedDuration.toFixed(2) + 's',
          endPoint: endPoint.toFixed(2) + 's'
        };
      }));
      
      console.log('🎵 Starting audio merge with tracks:', tracks.map(t => ({
        id: t.id,
        name: t.name,
        volume: t.volume,
        muted: t.muted,
        type: t.type
      })));
      
      // Load and merge each track
      for (const track of tracks) {
        console.log(`🎛️ Track Settings:`, {
          name: track.name || track.id,
          trackVolume: track.volume,
          trackMuted: track.muted,
          masterVolume: masterVolume,
          isMasterMuted: isMasterMuted
        });
        
        // Check if track should be skipped (handle both muted and isMuted properties)
        const isMuted = track.muted === true || track.isMuted === true;
        const hasZeroVolume = (track.volume === 0);
        const shouldSkip = isMuted || hasZeroVolume;
        
        console.log(`🔍 Should skip track "${track.name || track.id}"?`, shouldSkip, {
          muted: track.muted,
          isMuted: track.isMuted,
          isMutedResult: isMuted,
          volume: track.volume,
          hasZeroVolume: hasZeroVolume
        });
        
        if (shouldSkip) {
          console.log(`⏭️ Skipping track: ${track.name || track.id} (muted: ${isMuted}, zero volume: ${hasZeroVolume})`);
          continue;
        }
        
        try {
          console.log(`🎧 Processing track: ${track.name || track.id}`);
          
          // Fetch audio data
          const response = await fetch(track.url);
          if (!response.ok) throw new Error(`Failed to fetch ${track.url}`);
          
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Calculate track parameters with trim support
          const trackVolume = (track.volume || 1) * (isMasterMuted ? 0 : masterVolume);
          const startSample = Math.floor((track.startTime || 0) * sampleRate);
          
          // Respect trim settings
          const trimStartSeconds = track.trimStart || 0;
          const trimEndSeconds = track.trimEnd || audioBuffer.duration;
          const trimStartSample = Math.floor(trimStartSeconds * sampleRate);
          const trimEndSample = Math.floor(trimEndSeconds * sampleRate);
          const effectiveTrackSamples = Math.min(trimEndSample - trimStartSample, audioBuffer.length - trimStartSample);
          
          console.log(`🔊 Final Volume Calculation:`, {
            trackName: track.name || track.id,
            originalVolume: track.volume,
            masterVolume: masterVolume,
            isMasterMuted: isMasterMuted,
            finalTrackVolume: trackVolume,
            willBeAudible: trackVolume > 0,
            trimInfo: {
              trimStart: trimStartSeconds,
              trimEnd: trimEndSeconds,
              originalDuration: audioBuffer.duration,
              effectiveDuration: (trimEndSeconds - trimStartSeconds).toFixed(2) + 's',
              effectiveSamples: effectiveTrackSamples
            }
          });
          
          // Mix the track into the output buffer (respecting trim settings)
          const inputChannels = audioBuffer.numberOfChannels;
          
          if (inputChannels === 1) {
            // Mono input - mix to both left and right channels
            const inputData = audioBuffer.getChannelData(0);
            
            for (let i = 0; i < effectiveTrackSamples && (startSample + i) < totalSamples; i++) {
              const inputIndex = trimStartSample + i;
              const outputIndex = startSample + i;
              
              if (outputIndex >= 0 && inputIndex < audioBuffer.length) {
                let sample = inputData[inputIndex] * trackVolume;
                
                // Apply fade in/out if specified
                const fadeInSamples = Math.floor((track.fadeInDuration || 0) * sampleRate);
                const fadeOutSamples = Math.floor((track.fadeOutDuration || 0) * sampleRate);
                
                if (fadeInSamples > 0 && i < fadeInSamples) {
                  sample *= (i / fadeInSamples); // Fade in
                }
                
                if (fadeOutSamples > 0 && i >= (effectiveTrackSamples - fadeOutSamples)) {
                  const fadeOutProgress = (effectiveTrackSamples - i) / fadeOutSamples;
                  sample *= fadeOutProgress; // Fade out
                }
                
                leftChannel[outputIndex] += sample;
                rightChannel[outputIndex] += sample;
              }
            }
          } else {
            // Stereo or multi-channel input
            for (let channel = 0; channel < Math.min(numberOfChannels, inputChannels); channel++) {
              const inputData = audioBuffer.getChannelData(channel);
              const outputData = channel === 0 ? leftChannel : rightChannel;
              
              for (let i = 0; i < effectiveTrackSamples && (startSample + i) < totalSamples; i++) {
                const inputIndex = trimStartSample + i;
                const outputIndex = startSample + i;
                
                if (outputIndex >= 0 && inputIndex < audioBuffer.length) {
                  let sample = inputData[inputIndex] * trackVolume;
                  
                  // Apply fade in/out if specified
                  const fadeInSamples = Math.floor((track.fadeInDuration || 0) * sampleRate);
                  const fadeOutSamples = Math.floor((track.fadeOutDuration || 0) * sampleRate);
                  
                  if (fadeInSamples > 0 && i < fadeInSamples) {
                    sample *= (i / fadeInSamples); // Fade in
                  }
                  
                  if (fadeOutSamples > 0 && i >= (effectiveTrackSamples - fadeOutSamples)) {
                    const fadeOutProgress = (effectiveTrackSamples - i) / fadeOutSamples;
                    sample *= fadeOutProgress; // Fade out
                  }
                  
                  outputData[outputIndex] += sample;
                }
              }
            }
          }
          
          console.log(`✅ Successfully mixed: ${track.name || track.id}`);
          
        } catch (trackError) {
          console.error(`❌ Failed to process track ${track.name || track.id}:`, trackError);
          // Continue with other tracks
        }
      }
      
      // Convert to WAV blob
      const wavBlob = audioBufferToWav(mergedBuffer);
      
      // Save to backend and get S3 URL
      console.log('💾 Saving merged audio to backend...');
      const s3Url = await saveMergedAudioToBackend(wavBlob, projectId);
      
      if (s3Url) {
        // Don't auto-download, just show success notification and close modal
        // showNotification({
        //   title: 'Audio Merged & Saved!',
        //   message: `Combined ${tracks.length} tracks and saved as project's final audio`,
        //   type: 'success'
        // });
        
        // Close the modal after successful save
        onClose();
      } else {
        // Fallback: create local blob URL for download if backend save failed
        const downloadUrl = URL.createObjectURL(wavBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `merged-audio-${Date.now()}.wav`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);
        
        // showNotification({
        //   title: 'Audio Merged Successfully!',
        //   message: `Combined ${tracks.length} tracks (saved locally only)`,
        //   type: 'success'
        // });
        
        // Close the modal even for local save
        onClose();
      }
      
      // Clean up
      audioContext.close();
      
    } catch (error) {
      console.error('❌ Audio merge failed:', error);
      showNotification({
        title: 'Merge Failed',
        message: error instanceof Error ? error.message : 'Failed to merge audio tracks',
        type: 'error'
      });
    } finally {
      setIsMixing(false);
    }
  };

  // Helper function to save merged audio to backend and update project
  const saveMergedAudioToBackend = async (audioBlob: Blob, projectId?: string): Promise<string | null> => {
    try {
      console.log('🚀 Uploading merged audio to backend and updating project...');
      
      const formData = new FormData();
      formData.append('audio', audioBlob, `merged-audio-${Date.now()}.wav`);
      
      if (projectId) {
        formData.append('projectId', projectId);
      }
      
      const response = await fetch(getApiUrl('/music-generation/save-merged'), {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data?.s3Url) {
        console.log('✅ [AUDIO MIXER] Merged audio saved to S3:', result.data.s3Url);
        
        // Only auto-download for audio-only workflows, not video workflows
        if (isAudioOnlyWorkflow) {
          console.log('🔽 [AUDIO MIXER] Starting auto-download for audio-only workflow');
          
          // Use the download utility instead of window.open
          const filename = `merged-audio-${Date.now()}.wav`;
          
          console.log('🔽 [AUDIO MIXER] Calling  with:', {
            url: result.data.s3Url,
            filename,
            hasToken: !!token
          });

          await downloadAudio(result.data.s3Url, {
            filename: filename,
            onStart: () => {
              showNotification({
                title: 'Download Started!',
                message: 'Your media is downloading...',
                type: 'success'
              });
              console.log('📥 [EXPORT AUDIO] Calling');
            },
            onComplete: () => {
              console.log('✅ Downloaded successfully');
              showNotification({
                title: 'Download Completed!',
                message: 'Your media has been downloaded',
                type: 'success'
              });
            },
            onError: (error) => {
              console.error('❌ [SOUNDTRACK] Failed to download:', error);
              showNotification({
                title: 'Download Failed',
                message: 'Media was created but download failed. Use the download button to try again.',
                type: 'warning'
              });
            }
          });
          
          console.log('✅ [AUDIO MIXER]  completed');
        } else {
          console.log('⏭️ [AUDIO MIXER] Skipping auto-download (not audio-only workflow)');
        }
        
        // Verify project was updated with finalAudio field
        if (projectId && result.data.projectUpdated) {
          console.log('📝 [AUDIO MIXER] Project updated with finalAudio URL:', result.data.s3Url);
          console.log('🎯 [AUDIO MIXER] Project finalAudio field set to:', result.data.s3Url);
        }
        
        return result.data.s3Url;
      } else {
        console.error('❌ Backend save failed:', result.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Error saving merged audio to backend:', error);
      return null;
    }
  };
  const audioBufferToWav = (buffer: AudioBuffer): Blob => {
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    
    const data = [];
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        data.push(sample < 0 ? sample * 0x8000 : sample * 0x7FFF);
      }
    }
    
    const arrayBuffer = new ArrayBuffer(44 + data.length * bytesPerSample);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + data.length * bytesPerSample, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, data.length * bytesPerSample, true);
    
    // PCM data
    let offset = 44;
    for (let i = 0; i < data.length; i++) {
      view.setInt16(offset, data[i], true);
      offset += 2;
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  if (!isVisible) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FontAwesomeIcon icon={faSliders} style={{ marginRight: '12px' }} />
            {t('mixer.title')}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div style={{ padding: '32px' }}>
          {/* Track Information */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ 
                color: 'white', 
                margin: '0 0 16px 0',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faVolumeHigh} />
                {t('mixer.overview.title')}
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                color: '#a0a0a0'
              }}>
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.audioTracks')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#3b82f6', fontWeight: '600' }}>
                    {tracks.length}
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.totalDuration')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#8b5cf6', fontWeight: '600' }}>
                    {totalDuration.toFixed(1)}s
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.masterVolume')}
                  </div>
                  <div style={{ fontSize: '24px', color: '#22c55e', fontWeight: '600' }}>
                    {(masterVolume * 100).toFixed(0)}%
                  </div>
                </div>
                
                <div>
                  <div style={{ color: 'white', fontWeight: '500', marginBottom: '4px' }}>
                    {t('mixer.overview.status')}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: isMasterMuted ? '#ef4444' : '#22c55e',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {isMasterMuted ? t('mixer.overview.muted') : t('mixer.overview.active')}
                  </div>
                </div>
              </div>
            </div>

            {/* Track List */}
            {tracks.length > 0 && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <h3 style={{ 
                  color: 'white', 
                  margin: '0 0 16px 0',
                  fontSize: '18px'
                }}>
                  {t('mixer.trackDetails.title')}
                </h3>
                
                <div style={{ 
                  display: 'grid',
                  gap: '12px'
                }}>
                  {tracks.map((track, index) => (
                    <div key={track.id} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          color: 'white',
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          {track.name || `Track ${index + 1}`}
                        </div>
                        <div style={{ 
                          color: '#a0a0a0',
                          fontSize: '14px',
                          display: 'flex',
                          gap: '16px'
                        }}>
                          <span>{t('mixer.trackDetails.type')} {track.type}</span>
                          <span>{t('mixer.trackDetails.volume')} {track.volume === 0 ? '0' : (track.volume * 100).toFixed(0)}%</span>
                          {track.duration && <span>{t('mixer.trackDetails.duration')} {track.duration.toFixed(1)}s</span>}
                        </div>
                      </div>
                      
                      <div style={{ 
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: (track.muted === true || track.isMuted === true || track.volume === 0) ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        color: (track.muted === true || track.isMuted === true || track.volume === 0) ? '#ef4444' : '#22c55e'
                      }}>
                        {(track.muted === true || track.isMuted === true || track.volume === 0) ? t('mixer.overview.muted') : t('mixer.overview.active')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalActions}>
          <button onClick={onClose} className={styles.cancelButton}>
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            {t('mixer.actions.cancel')}
          </button>
          <button 
            onClick={handleMix}
            disabled={isMixing || tracks.length === 0}
            className={styles.primaryButton}
            style={{ 
              opacity: isMixing || tracks.length === 0 ? 0.6 : 1,
              cursor: isMixing || tracks.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            <FontAwesomeIcon icon={faCog} style={{ marginRight: '8px' }} />
            {isMixing ? t('mixer.actions.exporting') : t('mixer.actions.exportFull')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioMixer;
